/**
 * ScoringAuditPage
 *
 * #78-C Phase 1: Read-Only Audit MVP UI.
 * Platform Admin only — shows scoring run history for a member,
 * determinism verdict panel, factor snapshot viewer, and masked export.
 *
 * Access gate (client mirror of server): 'Platform Admin' role only.
 * Server guard is authoritative — client gate is UX only, not security.
 */

import { FC, useState, useCallback, useEffect } from 'react';
import {
  FiSearch,
  FiDownload,
  FiCheckCircle,
  FiAlertTriangle,
  FiXCircle,
  FiInfo,
  FiChevronDown,
  FiChevronRight,
  FiChevronLeft,
  FiX,
  FiCalendar,
} from 'react-icons/fi';
import {
  getScoringAudit,
  exportMaskedAuditJson,
  compareScoringRuns,
  getEvidencePack,
  type ScoringAuditRun,
  type ScoringAuditResponse,
  type CompareResponse,
  type CausalityLabel,
} from '../services/adminScoringAuditService';

// ─── Page size options ────────────────────────────────────────────────────────
const PAGE_SIZE_OPTIONS = [20, 50, 100] as const;

// ─── Date preset helpers ──────────────────────────────────────────────────────
function isoDate(d: Date): string { return d.toISOString().slice(0, 10); }
function getDatePreset(preset: string): { from: string; to: string } {
  const now = new Date();
  const to = isoDate(now);
  switch (preset) {
    case 'last7': {
      const d = new Date(now); d.setDate(d.getDate() - 7);
      return { from: isoDate(d), to };
    }
    case 'last30': {
      const d = new Date(now); d.setDate(d.getDate() - 30);
      return { from: isoDate(d), to };
    }
    case 'thisMonth': {
      return { from: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`, to };
    }
    case 'lastMonth': {
      const y = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
      const m = now.getMonth() === 0 ? 12 : now.getMonth();
      const lastDay = new Date(y, m, 0).getDate();
      return { from: `${y}-${String(m).padStart(2, '0')}-01`, to: `${y}-${String(m).padStart(2, '0')}-${lastDay}` };
    }
    default: return { from: '', to: '' };
  }
}

// ─── Verdict badge ──────────────────────────────────────────────────────────
const VERDICT_CONFIG: Record<string, { icon: FC<{ size?: number }>; color: string; bg: string; label: string }> = {
  MATCH:             { icon: FiCheckCircle,  color: 'text-green-700', bg: 'bg-green-50 border-green-200',   label: 'MATCH' },
  INPUT_CHANGED:     { icon: FiInfo,         color: 'text-blue-700',  bg: 'bg-blue-50 border-blue-200',    label: 'INPUT CHANGED' },
  VERSION_CHANGED:   { icon: FiAlertTriangle,color: 'text-amber-700', bg: 'bg-amber-50 border-amber-200',  label: 'VERSION CHANGED' },
  CONFIG_CHANGED:    { icon: FiAlertTriangle,color: 'text-amber-700', bg: 'bg-amber-50 border-amber-200',  label: 'CONFIG CHANGED' },
  OUTPUT_DRIFT:      { icon: FiXCircle,      color: 'text-red-700',   bg: 'bg-red-50 border-red-200',     label: 'OUTPUT DRIFT' },
  REPLAY_ERROR:      { icon: FiXCircle,      color: 'text-red-700',   bg: 'bg-red-50 border-red-200',     label: 'REPLAY ERROR' },
  FIRST_RUN:         { icon: FiInfo,         color: 'text-slate-600',  bg: 'bg-slate-50 border-slate-200', label: 'FIRST RUN' },
  INSUFFICIENT_DATA: { icon: FiInfo,         color: 'text-slate-500',  bg: 'bg-slate-50 border-slate-200', label: 'INSUFFICIENT DATA' },
};

const VerdictBadge: FC<{ verdict: string | null }> = ({ verdict }) => {
  const v = verdict || 'FIRST_RUN';
  const cfg = VERDICT_CONFIG[v] || VERDICT_CONFIG['INSUFFICIENT_DATA'];
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded border ${cfg.bg} ${cfg.color}`}>
      <Icon size={12} /> {cfg.label}
    </span>
  );
};

// ─── Fingerprint display ────────────────────────────────────────────────────
const Fp: FC<{ value: string | null; label: string }> = ({ value, label }) => (
  <div className="flex flex-col">
    <span className="text-[10px] text-slate-400 uppercase tracking-wider">{label}</span>
    <span className="font-mono text-xs text-slate-600 truncate max-w-[180px]" title={value || '—'}>
      {value ? `${value.substring(0, 12)}…` : '—'}
    </span>
  </div>
);

// ─── Factor detail row ─────────────────────────────────────────────────────
const FactorRow: FC<{ factors: Record<string, unknown>[] | null }> = ({ factors }) => {
  const [open, setOpen] = useState(false);
  if (!factors || factors.length === 0) return <span className="text-xs text-slate-400">No factors</span>;
  return (
    <div>
      <button onClick={() => setOpen(!open)} className="flex items-center gap-1 text-xs text-blue-600 hover:underline">
        {open ? <FiChevronDown size={12} /> : <FiChevronRight size={12} />}
        {factors.length} factor(s)
      </button>
      {open && (
        <pre className="mt-1 p-2 bg-slate-50 rounded text-[11px] text-slate-700 overflow-x-auto max-h-40 border border-slate-200">
          {JSON.stringify(factors, null, 2)}
        </pre>
      )}
    </div>
  );
};

// ─── Causality badge ─────────────────────────────────────────────────────────
const CAUSALITY_STYLE: Record<CausalityLabel, { color: string; bg: string; label: string }> = {
  UNCHANGED:                    { color: 'text-slate-500', bg: 'bg-slate-50 border-slate-200',  label: 'Unchanged' },
  NEW_DATA_INGESTION:           { color: 'text-blue-700',  bg: 'bg-blue-50 border-blue-200',   label: 'New Data' },
  DATA_CORRECTION:              { color: 'text-amber-700', bg: 'bg-amber-50 border-amber-200', label: 'Data Correction' },
  SYSTEM_TRANSFORMATION_CHANGE: { color: 'text-purple-700',bg: 'bg-purple-50 border-purple-200',label: 'System Change' },
  UNKNOWN:                      { color: 'text-red-700',   bg: 'bg-red-50 border-red-200',     label: 'Unknown' },
};

const CausalityBadge: FC<{ causality: CausalityLabel }> = ({ causality }) => {
  const s = CAUSALITY_STYLE[causality] || CAUSALITY_STYLE.UNKNOWN;
  return <span className={`inline-block px-1.5 py-0.5 text-[10px] font-semibold rounded border ${s.bg} ${s.color}`}>{s.label}</span>;
};

// ─── Compare panel ──────────────────────────────────────────────────────────
const ComparePanel: FC<{ data: CompareResponse; onClose: () => void }> = ({ data, onClose }) => {
  const verdictCfg = VERDICT_CONFIG[data.drift_verdict] || VERDICT_CONFIG['INSUFFICIENT_DATA'];
  const VerdictIcon = verdictCfg.icon;
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h2 className="text-sm font-semibold text-slate-700">Run Comparison</h2>
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded border ${verdictCfg.bg} ${verdictCfg.color}`}>
            <VerdictIcon size={12} /> {verdictCfg.label}
          </span>
          {data.is_output_drift && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-bold rounded border bg-red-100 border-red-300 text-red-800">
              🚨 OUTPUT DRIFT — Escalation Required
            </span>
          )}
        </div>
        <button onClick={onClose} className="text-xs text-slate-400 hover:text-slate-600">✕ Close</button>
      </div>

      {/* Score summary */}
      <div className="grid grid-cols-3 gap-4 mb-4 text-center">
        <div className="bg-slate-50 rounded-lg p-3">
          <div className="text-[10px] text-slate-400 uppercase">Run A</div>
          <div className="text-lg font-bold">{data.score_diff.run_a ?? '—'}</div>
          <div className="text-[10px] text-slate-400 font-mono">{new Date(data.run_a.run_ts).toLocaleDateString()}</div>
        </div>
        <div className="bg-slate-50 rounded-lg p-3 flex flex-col items-center justify-center">
          <div className="text-[10px] text-slate-400 uppercase">Δ Score</div>
          <div className={`text-lg font-bold ${(data.score_diff.delta ?? 0) > 0 ? 'text-green-600' : (data.score_diff.delta ?? 0) < 0 ? 'text-red-600' : 'text-slate-600'}`}>
            {data.score_diff.delta != null ? (data.score_diff.delta > 0 ? '+' : '') + data.score_diff.delta : '—'}
          </div>
        </div>
        <div className="bg-slate-50 rounded-lg p-3">
          <div className="text-[10px] text-slate-400 uppercase">Run B</div>
          <div className="text-lg font-bold">{data.score_diff.run_b ?? '—'}</div>
          <div className="text-[10px] text-slate-400 font-mono">{new Date(data.run_b.run_ts).toLocaleDateString()}</div>
        </div>
      </div>

      {/* Field diffs table */}
      <div className="text-xs text-slate-500 mb-2">{data.changed_field_count} of {data.total_field_count} field(s) changed</div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-50 text-left text-[10px] text-slate-500 uppercase tracking-wider">
              <th className="px-3 py-1.5">Field</th>
              <th className="px-3 py-1.5 text-right">Run A</th>
              <th className="px-3 py-1.5 text-right">Run B</th>
              <th className="px-3 py-1.5 text-right">Delta</th>
              <th className="px-3 py-1.5">Causality</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.field_diffs.map(d => (
              <tr key={d.field} className={d.causality !== 'UNCHANGED' ? 'bg-amber-50/30' : ''}>
                <td className="px-3 py-1.5 text-slate-600">{d.field.replace(/_/g, ' ')}</td>
                <td className="px-3 py-1.5 text-right font-mono">{d.run_a != null ? String(d.run_a) : '—'}</td>
                <td className="px-3 py-1.5 text-right font-mono">{d.run_b != null ? String(d.run_b) : '—'}</td>
                <td className={`px-3 py-1.5 text-right font-mono ${(d.delta ?? 0) !== 0 ? 'font-bold' : ''}`}>
                  {d.delta != null ? (d.delta > 0 ? '+' : '') + d.delta : '—'}
                </td>
                <td className="px-3 py-1.5"><CausalityBadge causality={d.causality} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Fingerprint comparison */}
      <div className="mt-4 grid grid-cols-2 gap-3 text-[10px]">
        <div><span className="text-slate-400 uppercase">Inputs FP A:</span> <span className="font-mono text-slate-600">{data.run_a.raw_inputs_fingerprint?.substring(0, 16) ?? '—'}…</span></div>
        <div><span className="text-slate-400 uppercase">Inputs FP B:</span> <span className="font-mono text-slate-600">{data.run_b.raw_inputs_fingerprint?.substring(0, 16) ?? '—'}…</span></div>
        <div><span className="text-slate-400 uppercase">Outputs FP A:</span> <span className="font-mono text-slate-600">{data.run_a.raw_outputs_fingerprint?.substring(0, 16) ?? '—'}…</span></div>
        <div><span className="text-slate-400 uppercase">Outputs FP B:</span> <span className="font-mono text-slate-600">{data.run_b.raw_outputs_fingerprint?.substring(0, 16) ?? '—'}…</span></div>
      </div>
    </div>
  );
};

// ─── Main page ──────────────────────────────────────────────────────────────
const ScoringAuditPage: FC = () => {
  const [memberId, setMemberId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ScoringAuditResponse | null>(null);
  const [selectedRun, setSelectedRun] = useState<ScoringAuditRun | null>(null);
  const [compareRunIds, setCompareRunIds] = useState<string[]>([]);
  const [compareResult, setCompareResult] = useState<CompareResponse | null>(null);
  const [comparing, setComparing] = useState(false);
  const [downloadingPack, setDownloadingPack] = useState(false);

  // #78-C P1-A: Pagination state
  const [pageSize, setPageSize] = useState<number>(20);
  const [currentPage, setCurrentPage] = useState(0); // 0-based page index

  // #78-C P1-B: Date range filter state
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // Track the last-searched identifier for re-fetches on page/filter change
  const [activeIdentifier, setActiveIdentifier] = useState('');

  // Core fetch function — used by search and pagination/filter changes
  const fetchAudit = useCallback(async (identifier: string, page: number, limit: number, from: string, to: string) => {
    if (!identifier) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getScoringAudit({
        identifier,
        limit,
        offset: page * limit,
        fromDate: from || undefined,
        toDate: to || undefined,
      });
      setResult(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch scoring audit');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSearch = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    const id = memberId.trim();
    if (!id) return;
    setCurrentPage(0);
    setSelectedRun(null);
    setCompareRunIds([]);
    setCompareResult(null);
    setActiveIdentifier(id);
    await fetchAudit(id, 0, pageSize, fromDate, toDate);
  }, [memberId, pageSize, fromDate, toDate, fetchAudit]);

  // Re-fetch when page, pageSize, or date range changes (only if we have an active search)
  useEffect(() => {
    if (activeIdentifier) {
      fetchAudit(activeIdentifier, currentPage, pageSize, fromDate, toDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize]);

  // When date filters change, reset to page 0 and re-fetch
  const applyDateFilter = useCallback(() => {
    if (!activeIdentifier) return;
    setCurrentPage(0);
    fetchAudit(activeIdentifier, 0, pageSize, fromDate, toDate);
  }, [activeIdentifier, pageSize, fromDate, toDate, fetchAudit]);

  const clearDateFilter = useCallback(() => {
    setFromDate('');
    setToDate('');
    if (activeIdentifier) {
      setCurrentPage(0);
      fetchAudit(activeIdentifier, 0, pageSize, '', '');
    }
  }, [activeIdentifier, pageSize, fetchAudit]);

  const applyPreset = useCallback((preset: string) => {
    const { from, to } = getDatePreset(preset);
    setFromDate(from);
    setToDate(to);
    if (activeIdentifier) {
      setCurrentPage(0);
      fetchAudit(activeIdentifier, 0, pageSize, from, to);
    }
  }, [activeIdentifier, pageSize, fetchAudit]);

  // Pagination helpers
  const totalCount = result?.total_count ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const rangeStart = totalCount === 0 ? 0 : currentPage * pageSize + 1;
  const rangeEnd = Math.min((currentPage + 1) * pageSize, totalCount);

  const handleExport = useCallback(() => {
    if (!result) return;
    const json = exportMaskedAuditJson(result.runs);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `scoring-audit-${result.member_id}-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [result]);

  const handleDownloadEvidencePack = useCallback(async (run: ScoringAuditRun) => {
    setDownloadingPack(true);
    try {
      const pack = await getEvidencePack(run.run_id);
      const json = JSON.stringify(pack, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `evidence-pack-${run.run_id.substring(0, 8)}-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.message || 'Failed to generate evidence pack');
    } finally {
      setDownloadingPack(false);
    }
  }, []);

  const toggleCompareRun = useCallback((runId: string) => {
    setCompareRunIds(prev => {
      if (prev.includes(runId)) return prev.filter(id => id !== runId);
      if (prev.length >= 2) return [prev[1], runId]; // replace oldest
      return [...prev, runId];
    });
    setCompareResult(null);
  }, []);

  const handleCompare = useCallback(async () => {
    if (compareRunIds.length !== 2) return;
    setComparing(true);
    setError(null);
    try {
      const data = await compareScoringRuns(compareRunIds[0], compareRunIds[1]);
      setCompareResult(data);
    } catch (err: any) {
      setError(err.message || 'Compare failed');
    } finally {
      setComparing(false);
    }
  }, [compareRunIds]);

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Financial Health Score Audit Evidence</h1>
          <p className="text-sm text-slate-500 mt-1">#78-C Determinism Proof Engine — Read-Only</p>
        </div>
        {result && result.runs.length > 0 && (
          <button
            onClick={handleExport}
            className="inline-flex items-center gap-2 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-800 text-sm font-medium"
          >
            <FiDownload size={14} /> Export Masked JSON
          </button>
        )}
      </div>

      {/* Search */}
      <section className="bg-white rounded-xl border border-slate-200 p-4 mb-6 shadow-sm">
        <form onSubmit={handleSearch} className="flex items-end gap-3">
          <div className="flex-1">
            <label className="block text-xs font-medium text-slate-600 mb-1">Member UUID, Email, or Auth0 ID</label>
            <input
              type="text"
              value={memberId}
              onChange={(e) => setMemberId(e.target.value)}
              placeholder="UUID, email@example.com, or auth0|abc123"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <button
            type="submit"
            disabled={loading || !memberId.trim()}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
          >
            <FiSearch size={14} />
            {loading ? 'Loading…' : 'Load Runs'}
          </button>
        </form>
        {result?.resolved_from && (
          <p className="mt-2 text-xs text-blue-600">
            Resolved from {result.resolved_from.type === 'email' ? 'email' : 'Auth0 ID'}: <span className="font-mono">{result.resolved_from.value}</span> → UUID <span className="font-mono">{result.member_id}</span>
          </p>
        )}
        <p className="mt-2 text-xs text-slate-500">
          All requests are logged with purpose <span className="font-mono">compliance_audit</span>. No raw PII is displayed.
        </p>
      </section>

      {/* #78-C P1-B: Date Range Filter + P1-A: Page Size */}
      {activeIdentifier && (
        <section className="bg-white rounded-xl border border-slate-200 p-4 mb-6 shadow-sm">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1"><FiCalendar className="inline mr-1" size={12} />From</label>
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
                className="px-2 py-1.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">To</label>
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
                className="px-2 py-1.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
            </div>
            <button onClick={applyDateFilter} disabled={loading}
              className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 disabled:opacity-50">
              Apply
            </button>
            {(fromDate || toDate) && (
              <button onClick={clearDateFilter}
                className="px-3 py-1.5 border border-slate-300 text-slate-600 rounded-lg text-xs font-medium hover:bg-slate-50">
                Clear
              </button>
            )}
            <div className="border-l border-slate-200 pl-3 flex gap-1.5">
              {(['last7', 'last30', 'thisMonth', 'lastMonth'] as const).map((p) => (
                <button key={p} onClick={() => applyPreset(p)}
                  className="px-2 py-1.5 border border-slate-200 text-slate-500 rounded text-[11px] hover:bg-slate-50 hover:text-slate-700">
                  {p === 'last7' ? '7d' : p === 'last30' ? '30d' : p === 'thisMonth' ? 'This mo' : 'Last mo'}
                </button>
              ))}
            </div>
            <div className="ml-auto flex items-end gap-2">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Per page</label>
                <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(0); }}
                  className="px-2 py-1.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
                  {PAGE_SIZE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
          </div>
          {(fromDate || toDate) && (
            <p className="mt-2 text-xs text-blue-600">
              Filtering: {fromDate || '…'} → {toDate || '…'}
              {result ? ` (${totalCount} total run${totalCount !== 1 ? 's' : ''})` : ''}
            </p>
          )}
        </section>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-6 flex items-start gap-3">
          <FiAlertTriangle className="mt-0.5 flex-shrink-0" size={18} />
          <div className="flex-1">
            <p className="text-sm font-medium">{error}</p>
          </div>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600 flex-shrink-0">
            <FiX size={16} />
          </button>
        </div>
      )}

      {/* Compare bar */}
      {result && result.runs.length >= 2 && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 mb-4 flex items-center justify-between">
          <div className="text-xs text-indigo-700">
            <strong>Compare Mode:</strong> Select 2 runs from the table below ({compareRunIds.length}/2 selected)
            {compareRunIds.length === 2 && <span className="ml-2 font-mono">{compareRunIds[0].substring(0, 8)}… vs {compareRunIds[1].substring(0, 8)}…</span>}
          </div>
          <div className="flex gap-2">
            {compareRunIds.length > 0 && (
              <button onClick={() => { setCompareRunIds([]); setCompareResult(null); }}
                className="text-xs px-3 py-1 rounded border border-indigo-300 text-indigo-600 hover:bg-indigo-100">
                Clear
              </button>
            )}
            <button onClick={handleCompare}
              disabled={compareRunIds.length !== 2 || comparing}
              className="text-xs px-3 py-1 rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed font-medium">
              {comparing ? 'Comparing…' : 'Compare Runs'}
            </button>
          </div>
        </div>
      )}

      {/* Compare result panel */}
      {compareResult && (
        <ComparePanel data={compareResult} onClose={() => setCompareResult(null)} />
      )}

      {/* Two-column layout: Run History + Detail */}
      {result && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Run History Table */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-slate-700">
                Run History
              </h2>
              <span className="text-xs text-slate-500">
                {totalCount > 0
                  ? `Showing ${rangeStart}–${rangeEnd} of ${totalCount} run${totalCount !== 1 ? 's' : ''}`
                  : '0 runs'}
              </span>
            </div>
            {/* Verdict Legend */}
            <div className="px-4 py-2 border-b border-slate-100 bg-white flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-500">
              <span className="font-medium text-slate-400 uppercase tracking-wider">Verdicts:</span>
              <span><span className="text-emerald-600 font-medium">✅ MATCH</span> — deterministic (same inputs → same score)</span>
              <span><span className="text-blue-600 font-medium">ℹ️ INPUT CHANGED</span> — inputs changed, output variance expected</span>
              <span><span className="text-amber-600 font-medium">⚠️ INSUFFICIENT DATA</span> — no prior run to compare</span>
              <span><span className="text-red-600 font-medium">🔴 OUTPUT DRIFT</span> — same inputs, different output — investigate</span>
            </div>
            {result.runs.length === 0 ? (
              <div className="p-8 text-center text-slate-400 text-sm">
                {fromDate || toDate
                  ? 'No scoring runs found for this date range.'
                  : 'No scoring runs found for this member.'}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 text-left text-xs text-slate-500 uppercase tracking-wider">
                      <th className="px-2 py-2 w-8">⚖</th>
                      <th className="px-4 py-2">Run Time</th>
                      <th className="px-4 py-2">Score</th>
                      <th className="px-4 py-2">Rating</th>
                      <th className="px-4 py-2">Version</th>
                      <th className="px-4 py-2">Verdict</th>
                      <th className="px-4 py-2">Factors</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {result.runs.map((run) => (
                      <tr
                        key={run.run_id}
                        onClick={() => setSelectedRun(run)}
                        className={`cursor-pointer hover:bg-blue-50/50 transition-colors ${
                          selectedRun?.run_id === run.run_id ? 'bg-blue-50' : ''
                        } ${compareRunIds.includes(run.run_id) ? 'ring-1 ring-indigo-300 bg-indigo-50/30' : ''}`}
                      >
                        <td className="px-2 py-2.5 text-center" onClick={(e) => { e.stopPropagation(); toggleCompareRun(run.run_id); }}>
                          <input type="checkbox" checked={compareRunIds.includes(run.run_id)} readOnly
                            className="w-3.5 h-3.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer" />
                        </td>
                        <td className="px-4 py-2.5 font-mono text-xs">
                          {new Date(run.run_ts).toLocaleString()}
                        </td>
                        <td className="px-4 py-2.5 font-bold">{run.verascore ?? '—'}</td>
                        <td className="px-4 py-2.5">{run.rating ?? '—'}</td>
                        <td className="px-4 py-2.5 font-mono text-xs">{run.pipeline_version || run.algorithm_version}</td>
                        <td className="px-4 py-2.5"><VerdictBadge verdict={run.determinism_verdict} /></td>
                        <td className="px-4 py-2.5"><FactorRow factors={run.factors} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* #78-C P1-A: Pagination Controls */}
            {totalCount > 0 && (
              <div className="px-4 py-3 border-t border-slate-100 bg-slate-50 flex items-center justify-between">
                <span className="text-xs text-slate-500">
                  Page {currentPage + 1} of {totalPages}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                    disabled={currentPage === 0 || loading}
                    className="inline-flex items-center gap-1 px-3 py-1.5 border border-slate-300 rounded text-xs text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <FiChevronLeft size={12} /> Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                    disabled={currentPage >= totalPages - 1 || loading}
                    className="inline-flex items-center gap-1 px-3 py-1.5 border border-slate-300 rounded text-xs text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Next <FiChevronRight size={12} />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Detail / Verdict Panel */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
            <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
              <h2 className="text-sm font-semibold text-slate-700">
                {selectedRun ? 'Run Detail' : 'Select a run →'}
              </h2>
            </div>
            {selectedRun ? (
              <div className="p-4 space-y-4">
                {/* Determinism Verdict */}
                <div>
                  <div className="text-xs text-slate-400 uppercase tracking-wider mb-1">Determinism Verdict</div>
                  <VerdictBadge verdict={selectedRun.determinism_verdict} />
                </div>

                {/* Fingerprints */}
                <div className="grid grid-cols-2 gap-3">
                  <Fp label="Inputs FP" value={selectedRun.raw_inputs_fingerprint} />
                  <Fp label="Outputs FP" value={selectedRun.raw_outputs_fingerprint} />
                  <Fp label="Config Hash" value={selectedRun.config_hash} />
                  <Fp label="Prev Inputs FP" value={selectedRun.prev_inputs_fp} />
                  <Fp label="Prev Outputs FP" value={selectedRun.prev_outputs_fp} />
                  <Fp label="Data Cutoff" value={selectedRun.data_cutoff_timestamp} />
                </div>

                {/* Run metadata */}
                <div className="border-t border-slate-100 pt-3 space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-400">Run ID</span>
                    <span className="font-mono text-slate-600 truncate max-w-[160px]" title={selectedRun.run_id}>{selectedRun.run_id.substring(0, 8)}…</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-400">Environment</span>
                    <span className="text-slate-600">{selectedRun.environment}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-400">Algorithm</span>
                    <span className="font-mono text-slate-600">{selectedRun.algorithm_version}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-400">Pipeline</span>
                    <span className="font-mono text-slate-600">{selectedRun.pipeline_version}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-400">Status</span>
                    <span className="text-slate-600">{selectedRun.status}</span>
                  </div>
                  {selectedRun.prev_run_id && (
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-400">Compared to</span>
                      <span className="font-mono text-slate-600 truncate max-w-[160px]" title={selectedRun.prev_run_id}>{selectedRun.prev_run_id.substring(0, 8)}…</span>
                    </div>
                  )}
                </div>

                {/* Aggregate Inputs (Level 1) */}
                {selectedRun.inputs && (
                  <div className="border-t border-slate-100 pt-3">
                    <div className="text-xs text-slate-400 uppercase tracking-wider mb-2">Inputs (Level 1 Aggregate)</div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                      {Object.entries(selectedRun.inputs).map(([k, v]) => (
                        <div key={k} className="flex justify-between text-xs">
                          <span className="text-slate-400 truncate max-w-[100px]" title={k}>{k.replace(/_/g, ' ')}</span>
                          <span className="text-slate-700 font-mono">{v != null ? String(v) : '—'}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Evidence Pack Download */}
                <div className="border-t border-slate-100 pt-3">
                  <button
                    onClick={() => handleDownloadEvidencePack(selectedRun)}
                    disabled={downloadingPack}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors"
                  >
                    <FiDownload size={14} />
                    {downloadingPack ? 'Generating…' : 'Download Evidence Pack'}
                  </button>
                  <p className="text-[10px] text-slate-400 mt-1.5 text-center">
                    Regulator-grade JSON — hashes, verdict, factors, audit trail. No raw PII.
                  </p>
                </div>
              </div>
            ) : (
              <div className="p-6 space-y-4">
                <div className="text-center">
                  <div className="text-slate-300 mb-3">
                    <FiInfo size={28} className="mx-auto" />
                  </div>
                  <p className="text-sm font-medium text-slate-600 mb-2">Select a run to inspect</p>
                  <p className="text-xs text-slate-400 leading-relaxed max-w-[220px] mx-auto">
                    Each row is a scoring run with a determinism verdict — proof that identical inputs produce the same score. Click any row to view its fingerprints, factors, and downloadable evidence pack.
                  </p>
                </div>
                <div className="border-t border-slate-100 pt-3">
                  <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium mb-2">Quick Reference</p>
                  <div className="space-y-1.5 text-[11px]">
                    <div className="flex gap-2"><span className="text-slate-400 w-20 flex-shrink-0">Fingerprint</span><span className="text-slate-500">SHA-256 hash of run inputs or outputs</span></div>
                    <div className="flex gap-2"><span className="text-slate-400 w-20 flex-shrink-0">Config Hash</span><span className="text-slate-500">Algorithm config snapshot identifier</span></div>
                    <div className="flex gap-2"><span className="text-slate-400 w-20 flex-shrink-0">Data Cutoff</span><span className="text-slate-500">Latest transaction date included</span></div>
                    <div className="flex gap-2"><span className="text-slate-400 w-20 flex-shrink-0">Evidence Pack</span><span className="text-slate-500">Regulator-grade JSON — no raw PII</span></div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ScoringAuditPage;
